import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, query, where, addDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { updateUserAPI } from '../services/serverService';
import { useAuth } from '../context/AuthContext';

// Import new modular components
import StatScrollRow from '../components/workDays/StatScrollRow';
import WeekNavigator from '../components/workDays/WeekNavigator';
import FilterDropdown from '../components/workDays/FilterDropdown';
import QuickAddModal from '../components/workDays/QuickAddModal';
import CreateFromAvailabilityModal from '../components/workDays/CreateFromAvailabilityModal';
import BulkTimingsModal from '../components/workDays/BulkTimingsModal';
import CancellationsModal from '../components/workDays/CancellationsModal';
import SelectRangeModal from '../components/workDays/SelectRangeModal';

import { FaPlus, FaBolt, FaCalendarDay, FaHistory, FaSyncAlt } from 'react-icons/fa';

// Helper functions for Flutter-parity logic
const isInactiveShift = (shift) => {
    if (!shift) return true;
    const status = (shift.shiftStatus || shift.status || '').toString().toLowerCase().trim();
    const deletionState = (shift.deletionStatus || '').toString().toLowerCase().trim();
    
    const hasCancelledFlag = shift.cancelled === true || shift.isCancelled === true;
    const hasDeletedFlag = shift.deleted === true || shift.isDeleted === true || shift.archived === true || shift.isArchived === true;
    
    const hasCancellationMeta = shift.cancelledAt != null || shift.cancelledBy != null || shift.cancellationReason != null;
    const hasDeletionMeta = shift.deletedAt != null || shift.deletedBy != null || shift.deletedReason != null;
    
    const inactiveStatuses = ['cancelled', 'canceled', 'deleted', 'removed', 'archived', 'inactive', 'void', 'voided', 'suspended'];
    
    if (inactiveStatuses.includes(status) || inactiveStatuses.includes(deletionState)) return true;
    if (hasCancelledFlag || hasDeletedFlag || hasCancellationMeta || hasDeletionMeta) return true;
    
    const activeStatuses = ['active', 'scheduled', 'in-progress', 'completed'];
    if (status !== '' && !activeStatuses.includes(status)) return true;
    
    return false;
};

const isInactiveLeave = (leave) => {
    if (!leave) return true;
    const status = (leave.leaveStatus || leave.status || '').toString().toLowerCase().trim();
    if (['cancelled', 'canceled', 'deleted', 'archived', 'removed'].includes(status)) return true;
    if (leave.cancelled === true || leave.deleted === true || leave.archived === true || leave.isDeleted === true) return true;
    if (leave.cancelledAt != null || leave.deletedAt != null || leave.archivedAt != null) return true;
    return false;
};

const getShiftHours = (shift) => {
    const parseDateTime = (dateStr, timeStr) => {
        if (!dateStr || typeof dateStr !== 'string' || !timeStr || typeof timeStr !== 'string') return null;
        const [yyyy, mm, dd] = dateStr.split('-');
        if (!yyyy || !mm || !dd) return null;
        const [hh, min] = timeStr.split(':');
        if (!hh || !min) return null;
        const h = Number(hh);
        const m = Number(min);
        if (isNaN(h) || isNaN(m)) return null;
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd), h, m);
    };

    const startDateStr = shift.shiftStartDate || shift.date;
    const endDateStr = shift.shiftEndDate || shift.endDate;
    const startTimeStr = shift.startTime; 
    const endTimeStr = shift.endTime;

    if (!startDateStr || !endDateStr || !startTimeStr || !endTimeStr) {
        return 0; // Match Flutter's strictness
    }

    const start = parseDateTime(startDateStr, startTimeStr);
    let end = parseDateTime(endDateStr, endTimeStr);

    if (!start || !end) return 0;

    if (end <= start) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
    }

    let diffMinutes = (end - start) / (1000 * 60);

    if (shift.isBreakAllowed === true) {
        const bStartDateStr = shift.breakStartDate || shift.shiftStartDate || shift.date;
        const bEndDateStr = shift.breakEndDate || shift.shiftEndDate || shift.endDate;
        const bStartTimeStr = shift.breakStartTime;
        const bEndTimeStr = shift.breakEndTime;
        
        if (bStartDateStr && bEndDateStr && bStartTimeStr && bEndTimeStr) {
            const bStart = parseDateTime(bStartDateStr, bStartTimeStr);
            let bEnd = parseDateTime(bEndDateStr, bEndTimeStr);
            if (bStart && bEnd) {
                if (bEnd <= bStart) {
                    bEnd = new Date(bEnd.getTime() + 24 * 60 * 60 * 1000);
                }
                diffMinutes -= (bEnd - bStart) / (1000 * 60);
            }
        }
    }

    return diffMinutes / 60;
};

export default function ManageWorkDaysPage() {
    const { serverId, employeeId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    
    // Employee Data
    const [employee, setEmployee] = useState(null);
    const [workingHours, setWorkingHours] = useState('');
    const [loading, setLoading] = useState(true);
    
    // Core Data State
    const [shifts, setShifts] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [availability, setAvailability] = useState({});
    const [availability30d, setAvailability30d] = useState(0);
    const [availabilityPeriod, setAvailabilityPeriod] = useState(4); // default 4 weeks
    
    // UI Navigation State
    const [activeTab, setActiveTab] = useState('availability'); // 'availability' or 'shifts'
    const [availSubTab, setAvailSubTab] = useState('available'); // 'available' or 'liveIn'
    const [shiftSubTab, setShiftSubTab] = useState('currentWeek'); // 'currentWeek' or 'filteredView'
    
    // Filters and Week Navigation
    const [currentFilter, setCurrentFilter] = useState('All');
    
    const handleFilterChange = (newFilter) => {
        setCurrentFilter(newFilter);
        // Automatically present the filtered view section when a filter is selected
        setActiveTab('shifts');
        setShiftSubTab('filteredView');
    };
    
    // Calculate start of current week (Monday)
    const getStartOfWeek = (d) => {
        const date = new Date(d);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.setDate(diff));
    };
    const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
    const [currentWeekEnd, setCurrentWeekEnd] = useState(() => {
        const d = getStartOfWeek(new Date());
        d.setDate(d.getDate() + 6);
        return d;
    });

    // Notification State
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [saving, setSaving] = useState(false);

    // Modal Visibility State
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showCreateFromAvail, setShowCreateFromAvail] = useState(false);
    const [showBulkTimings, setShowBulkTimings] = useState(false);
    const [showCancellations, setShowCancellations] = useState(false);
    const [showSelectRange, setShowSelectRange] = useState(false);

    // --- FETCH DATA ---
    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            // Path: servers/{serverId}/employees/{employeeId}
            const empRef = doc(db, 'servers', serverId, 'employees', employeeId);
            const empSnap = await getDoc(empRef);
            if (empSnap.exists()) {
                const data = { id: empSnap.id, ...empSnap.data() };
                setEmployee(data);
                const wh = data.employeeDetails?.workingHours ?? data.workingHours ?? 0;
                setWorkingHours(wh.toString());
            } else {
                throw new Error('Employee not found.');
            }

            // Path: servers/{serverId}/employeeShifts
            const shiftsRef = collection(db, 'servers', serverId, 'employeeShifts');
            const shiftsQ = query(shiftsRef, where('staffID', '==', employeeId));
            const shiftsSnap = await getDocs(shiftsQ);
            const parseTime24 = (timeStr) => {
                if (!timeStr) return '';
                let t = timeStr.toString().toUpperCase().trim();
                
                // Remove periods: "A.M." -> "AM", "P.M." -> "PM"
                t = t.replace(/\./g, '');
                
                // Ensure exactly one space before AM/PM
                t = t.replace(/\s*AM/g, ' AM').replace(/\s*PM/g, ' PM');
                
                if (t.includes(' AM') || t.includes(' PM')) {
                    const [time, modifier] = t.split(' ');
                    if (!time || !modifier) return timeStr;
                    let [hours, minutes] = time.split(':');
                    if (hours === '12') hours = '00';
                    if (modifier === 'PM') hours = (parseInt(hours, 10) + 12).toString();
                    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
                }
                return t;
            };

            const loadedShifts = shiftsSnap.docs.map(d => {
                const data = d.data();
                return {
                    id: d.id,
                    ...data,
                    date: data.shiftStartDate || data.date,
                    startTime: parseTime24(data.shiftStartTime || data.startTime),
                    endTime: parseTime24(data.shiftEndTime || data.endTime),
                    breakStartTime: parseTime24(data.breakStartTime),
                    breakEndTime: parseTime24(data.breakEndTime),
                    rawStartTime: data.shiftStartTime || data.startTime,
                    rawEndTime: data.shiftEndTime || data.endTime,
                };
            });
            loadedShifts.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
            setShifts(loadedShifts);

            // Path: servers/{serverId}/employeeLeaves
            const leavesRef = collection(db, 'servers', serverId, 'employeeLeaves');
            const leavesQ = query(leavesRef, where('staffID', '==', employeeId));
            const leavesSnap = await getDocs(leavesQ);
            const loadedLeaves = leavesSnap.docs.map(d => {
                const data = d.data();
                return { 
                    id: d.id, 
                    ...data,
                    startDate: data.leaveStartDate || data.startDate,
                    endDate: data.leaveEndDate || data.endDate,
                };
            });
            loadedLeaves.sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
            setLeaves(loadedLeaves);

            // Path: servers/{serverId}/employees/{employeeId}/availability
            const availRef = collection(db, 'servers', serverId, 'employees', employeeId, 'availability');
            const availSnap = await getDocs(availRef);
            let availMap = {};
            availSnap.forEach(d => { availMap[d.id] = d.data(); });
            
            if (Object.keys(availMap).length === 0) {
                availMap = {
                    Monday: { available: true },
                    Tuesday: { available: true },
                    Wednesday: { available: true },
                    Thursday: { available: true },
                    Friday: { available: true },
                    Saturday: { available: false },
                    Sunday: { available: false },
                    preferences: { liveInShift: false, period: 4 }
                };
            }
            setAvailability(availMap);
            if (availMap.preferences?.period) setAvailabilityPeriod(availMap.preferences.period);

            // Path: servers/{serverId}/employee_availability
            try {
                const todayStr = new Date().toISOString().split('T')[0];
                const end30 = new Date();
                end30.setDate(end30.getDate() + 30);
                const end30Str = end30.toISOString().split('T')[0];
                
                const empAvailRef = collection(db, 'servers', serverId, 'employee_availability');
                const empAvailQ = query(
                    empAvailRef, 
                    where('staffId', '==', employeeId),
                    where('availableDate', '>=', todayStr),
                    where('availableDate', '<=', end30Str)
                );
                const empAvailSnap = await getDocs(empAvailQ);
                setAvailability30d(empAvailSnap.docs.length);
            } catch (availErr) {
                console.error('Error fetching availabilityNext30Days:', availErr);
                setAvailability30d(0);
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to fetch employee details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (serverId && employeeId) fetchData();
    }, [serverId, employeeId]);

    const showMessage = (msg, isError = false) => {
        if (isError) { setError(msg); setSuccessMsg(''); } 
        else { setSuccessMsg(msg); setError(''); }
        setTimeout(() => { setError(''); setSuccessMsg(''); }, 5000);
    };

    // --- CALCULATE ANALYTICS ---
    // Calculate stats based on fetched shifts and leaves
    const calculatedStats = useMemo(() => {
        let weekH = 0, monthH = 0, quarterH = 0, yearH = 0;
        let cancelledS = 0, cancelledL = 0;
        
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        const d = now.getDate();
        
        const weekStart = new Date(y, m, d - (now.getDay() === 0 ? 6 : now.getDay() - 1));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        
        const monthStart = new Date(y, m, 1);
        const monthEnd = new Date(y, m + 1, 0, 23, 59, 59, 999);
        
        const quarterMonthStart = Math.floor(m / 3) * 3;
        const quarterStart = new Date(y, quarterMonthStart, 1);
        const quarterEnd = new Date(y, quarterMonthStart + 3, 0, 23, 59, 59, 999);
        
        const yearStart = new Date(m >= 3 ? y : y - 1, 3, 1); // April 1st
        const yearEnd = new Date(yearStart.getFullYear() + 1, 2, 31, 23, 59, 59, 999); // March 31st

        shifts.forEach(s => {
            if (isInactiveShift(s)) {
                cancelledS++;
                return;
            }
            
            if(!s.date) return;
            const [yyyy, mm, dd] = s.date.split('-');
            const sDate = new Date(yyyy, mm - 1, dd); 
            
            const hrs = getShiftHours(s);

            // Time boxing
            if (sDate >= weekStart && sDate <= weekEnd) weekH += hrs;
            if (sDate >= monthStart && sDate <= monthEnd) monthH += hrs;
            if (sDate >= quarterStart && sDate <= quarterEnd) quarterH += hrs;
            if (sDate >= yearStart && sDate <= yearEnd) yearH += hrs;
        });

        let usedAnnualLeave = 0;
        let usedSickLeave = 0;

        leaves.forEach(l => {
            if (isInactiveLeave(l)) {
                cancelledL++;
                return;
            }
            
            const type = (l.leaveType || l.leave_type || '').toString().toLowerCase();
            if(!l.startDate || !l.endDate) return;
            const [sy, sm, sd] = l.startDate.split('-');
            const lStart = new Date(sy, sm - 1, sd);
            const [ey, em, ed] = l.endDate.split('-');
            const lEnd = new Date(ey, em - 1, ed, 23, 59, 59, 999);
            
            if (lEnd < yearStart || lStart > yearEnd) return; // Outside financial year
            
            // Simplified day difference logic for overlapping part in the financial year
            const effStart = lStart < yearStart ? yearStart : lStart;
            const effEnd = lEnd > yearEnd ? yearEnd : lEnd;
            const diffDays = Math.floor((effEnd - effStart) / (1000 * 60 * 60 * 24)) + 1;
            
            if (type.includes('annual')) usedAnnualLeave += diffDays;
            if (type.includes('sick')) usedSickLeave += diffDays;
        });
        
        let allowance = 0;
        if (employee?.employeeDetails?.leaves) {
            allowance = parseFloat(employee.employeeDetails.leaves.totalAnnualLeaves || employee.employeeDetails.leaves.annualLeaveAllowance || 0);
        } else if (employee?.leaves) {
            allowance = parseFloat(employee.leaves.totalAnnualLeaves || employee.leaves.annualLeaveAllowance || 0);
        }

        return {
            weekPlanned: weekH, monthPlanned: monthH, quarterPlanned: quarterH, yearPlanned: yearH,
            availability30d: availability30d,
            annualLeaveLeft: Math.max(0, allowance - usedAnnualLeave),
            sickDaysUsed: usedSickLeave,
            cancelledShifts: cancelledS,
            cancelledLeave: cancelledL
        };
    }, [shifts, leaves, availability30d, employee]);

    const currentWeekStats = useMemo(() => {
        let hours = 0;
        let count = 0;

        shifts.forEach(s => {
            if (isInactiveShift(s)) return;
            if(!s.date) return;
            const [yyyy, mm, dd] = s.date.split('-');
            const sDate = new Date(yyyy, mm - 1, dd); 
            if (sDate >= currentWeekStart && sDate <= currentWeekEnd) {
                count++;
                hours += getShiftHours(s);
            }
        });
        return { shiftsCount: count, hours: hours };
    }, [shifts, currentWeekStart, currentWeekEnd]);


    // --- HANDLERS ---
    const handleQuickAdd = async (shiftData) => {
        setSaving(true);
        try {
            // Path: servers/{serverId}/shifts
            const shiftsRef = collection(db, 'servers', serverId, 'employeeShifts');
            const today = new Date().toISOString().split('T')[0];
            await addDoc(shiftsRef, {
                staffID: employeeId,
                staffName: `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim(),
                date: today,
                startTime: shiftData.startTime,
                endTime: shiftData.endTime,
                shiftStartDate: today,
                shiftStartTime: shiftData.startTime,
                shiftEndTime: shiftData.endTime,
                type: shiftData.type,
                is24Hour: shiftData.is24Hour,
                status: 'Scheduled',
                createdAt: new Date(),
                updatedAt: new Date()
            });
            showMessage('Quick shift added for today.');
            setShowQuickAdd(false);
            fetchData();
        } catch (err) {
            console.error(err);
            showMessage('Failed to add quick shift.', true);
        } finally {
            setSaving(false);
        }
    };

    const handleBulkTimingsSave = async (dates, activeBlocks) => {
        setSaving(true);
        try {
            const batch = writeBatch(db);
            const shiftsRef = collection(db, 'servers', serverId, 'employeeShifts');
            
            dates.forEach(dateStr => {
                activeBlocks.forEach(block => {
                    const newDocRef = doc(shiftsRef);
                    batch.set(newDocRef, {
                        staffID: employeeId,
                        staffName: `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim(),
                        date: dateStr,
                        startTime: block.startTime,
                        endTime: block.endTime,
                        shiftStartDate: dateStr,
                        shiftStartTime: block.startTime,
                        shiftEndTime: block.endTime,
                        type: block.type,
                        is24Hour: block.is24Hour,
                        status: 'Scheduled',
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                });
            });

            await batch.commit();
            showMessage(`Successfully created shifts for ${dates.length} days.`);
            setShowBulkTimings(false);
            fetchData();
        } catch (err) {
            console.error(err);
            showMessage('Failed to create bulk shifts.', true);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAvailability = async () => {
        setSaving(true);
        try {
            const batch = writeBatch(db);
            // Path: servers/{serverId}/employees/{employeeId}/availability
            const availRef = collection(db, 'servers', serverId, 'employees', employeeId, 'availability');
            
            const availToSave = { ...availability };
            availToSave.preferences = { ...availToSave.preferences, period: availabilityPeriod };

            Object.keys(availToSave).forEach(key => {
                const docRef = doc(availRef, key);
                batch.set(docRef, availToSave[key]);
            });

            await batch.commit();
            showMessage('Availability updated successfully.');
        } catch (err) {
            console.error(err);
            showMessage('Failed to save availability.', true);
        } finally {
            setSaving(false);
        }
    };


    if (loading) return <div style={{ padding: '24px', color: '#666' }}>Loading workdays data...</div>;

    // Filter shifts based on week view
    const displayShifts = shifts.filter(s => {
        const isInactive = isInactiveShift(s);
        if (currentFilter === 'All') return true;
        if (isInactive) return false; // Hide cancelled if not 'All'
        
        if (!s.date) return false;
        const [yyyy, mm, dd] = s.date.split('-');
        const sDate = new Date(yyyy, mm - 1, dd); // Local date
        
        const today = new Date();
        today.setHours(0,0,0,0);
        
        if (currentFilter === 'Today') return sDate.getTime() === today.getTime();
        
        const diffDays = (sDate - today) / (1000 * 60 * 60 * 24);
        if (currentFilter === 'Next 7 days') return diffDays >= 0 && diffDays <= 7;
        if (currentFilter === 'Next 14 days') return diffDays >= 0 && diffDays <= 14;
        if (currentFilter === 'Next 28 days') return diffDays >= 0 && diffDays <= 28;
        if (currentFilter === 'Previous Shifts') return diffDays < 0;
        
        return true;
    });

    const activeAvailableDaysCount = Object.keys(availability).filter(k => k !== 'preferences' && availability[k].available).length;
    // rough estimation for 4 weeks (30 days)
    const availableDaysNext30 = activeAvailableDaysCount * 4; 

    const isCurrentWeek = currentWeekStart.getTime() === getStartOfWeek(new Date()).getTime() && 
                          currentWeekEnd.getTime() === new Date(getStartOfWeek(new Date()).setDate(getStartOfWeek(new Date()).getDate() + 6)).getTime();
    const currentWeekLabel = isCurrentWeek 
        ? `Current Week Hours (${currentWeekStart.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit'})} - ${currentWeekEnd.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit'})})`
        : `Selected Week Hours (${currentWeekStart.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit'})} - ${currentWeekEnd.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit'})})`;

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', gap: '16px', backgroundColor: '#3b82f6', padding: '16px 24px', borderRadius: '8px', color: 'white' }}>
                <button 
                    onClick={() => navigate(`/servers/${serverId}/employees/${employeeId}`)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', color: 'white' }}
                >
                    &lt;
                </button>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Manage Work Days: {employee?.firstName} {employee?.lastName}</h1>
            </div>

            {error && <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '6px' }}>{error}</div>}
            {successMsg && <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fdf4', color: '#15803d', borderRadius: '6px' }}>{successMsg}</div>}

            {/* Top Cards row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <div style={{ flex: 1, backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '4px' }}>Working Hours</div>
                        <div style={{ fontSize: '16px', color: '#0056b3' }}>{workingHours}</div>
                    </div>
                    <span style={{ color: '#0056b3' }}>&gt;</span>
                </div>
                
                <div 
                    onClick={() => setShowSelectRange(true)}
                    style={{ flex: 1, backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                            {currentWeekLabel}
                        </span>
                        <span style={{ color: '#e11d48' }}>{'>'}</span>
                    </div>
                    <div style={{ color: '#e11d48', fontSize: '14px' }}>
                        {currentWeekStats.hours.toFixed(1)} hours
                    </div>
                </div>
                
                <div style={{ flex: 1, backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center' }}>
                    <FilterDropdown currentFilter={currentFilter} onSelectFilter={handleFilterChange} />
                </div>
            </div>

            {/* Analytics Dashboard */}
            <StatScrollRow stats={calculatedStats} />

            {/* Action Bar */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '4px' }}>
                <button style={styles.actionBtn} onClick={() => setShowQuickAdd(true)}>
                    <FaCalendarDay color="#3b82f6" /> Quick add today
                </button>
                <button style={styles.actionBtn} onClick={() => setShowCreateFromAvail(true)}>
                    <FaBolt color="#3b82f6" /> Create shifts from availability
                </button>
                <button style={styles.actionBtn} onClick={() => setShowBulkTimings(true)}>
                    <FaPlus color="#3b82f6" /> Bulk timings
                </button>
                <button style={styles.actionBtn} onClick={() => setShowCancellations(true)}>
                    <FaHistory color="#3b82f6" /> Cancellations
                </button>
                <button style={styles.actionBtn} onClick={fetchData}>
                    <FaSyncAlt color="#3b82f6" /> Refresh planning
                </button>
            </div>

            {/* Main Tabs (Availability vs Scheduled Shifts) */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', backgroundColor: 'white', borderRadius: '8px', padding: '4px', border: '1px solid #e5e7eb' }}>
                <button 
                    onClick={() => setActiveTab('availability')} 
                    style={{ ...styles.mainTabBtn, backgroundColor: activeTab === 'availability' ? '#0056b3' : 'transparent', color: activeTab === 'availability' ? 'white' : '#6b7280' }}
                >
                    Availability
                </button>
                <button 
                    onClick={() => setActiveTab('shifts')} 
                    style={{ ...styles.mainTabBtn, backgroundColor: activeTab === 'shifts' ? '#0056b3' : 'transparent', color: activeTab === 'shifts' ? 'white' : '#6b7280' }}
                >
                    Scheduled Shifts
                </button>
            </div>

            {/* Availability Content */}
            {activeTab === 'availability' && (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '24px' }}>
                    
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>View Availability</h3>
                    <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f3f4f6', padding: '4px', borderRadius: '8px', marginBottom: '24px' }}>
                        <button 
                            onClick={() => setAvailSubTab('available')}
                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', backgroundColor: availSubTab === 'available' ? '#0056b3' : 'transparent', color: availSubTab === 'available' ? 'white' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                        >
                            Available Days
                        </button>
                        <button 
                            onClick={() => setAvailSubTab('liveIn')}
                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', backgroundColor: availSubTab === 'liveIn' ? '#0056b3' : 'transparent', color: availSubTab === 'liveIn' ? 'white' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                        >
                            Live-in Days
                        </button>
                    </div>

                    {availSubTab === 'available' && (
                        <>
                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#1f2937' }}>Select Available Days</h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Choose the days when this employee is typically available for shifts</p>
                            
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '32px' }}>
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                    const isAvailable = availability[day]?.available || false;
                                    return (
                                        <button
                                            key={day}
                                            onClick={() => setAvailability(prev => ({ ...prev, [day]: { ...prev[day], available: !isAvailable } }))}
                                            style={{
                                                padding: '10px 20px',
                                                borderRadius: '24px',
                                                border: `1px solid ${isAvailable ? '#0056b3' : '#d1d5db'}`,
                                                backgroundColor: 'white',
                                                color: isAvailable ? '#0056b3' : '#4b5563',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
                                <div>
                                    <div style={{ fontWeight: '600', color: '#111827', fontSize: '16px' }}>Availability Period</div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Selected: {availabilityPeriod} weeks</div>
                                </div>
                                <select 
                                    value={availabilityPeriod} 
                                    onChange={(e) => setAvailabilityPeriod(Number(e.target.value))}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                >
                                    <option value={1}>1 week</option>
                                    <option value={2}>2 weeks</option>
                                    <option value={4}>4 weeks</option>
                                    <option value={8}>8 weeks</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontWeight: '600' }}>Available Days Overview</div>
                                <div style={{ fontSize: '12px', color: '#0056b3', backgroundColor: '#e6f0fa', padding: '4px 8px', borderRadius: '4px' }}>Next 30 Days</div>
                            </div>
                            <div style={{ backgroundColor: '#f4f6f8', border: '1px solid #cce0f5', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <FaCalendarDay color="#0056b3" size="24" />
                                <div>
                                    <div style={{ fontSize: '12px', color: '#004085' }}>Available Days</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#002752' }}>{availableDaysNext30}</div>
                                </div>
                            </div>
                            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>Approx {availableDaysNext30} available days scheduled for the next 30 days based on weekly pattern.</p>
                        </>
                    )}

                    {availSubTab === 'liveIn' && (
                        <>
                            <div style={{ marginBottom: '32px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={availability.preferences?.liveInShift || false} 
                                        onChange={() => setAvailability(prev => ({...prev, preferences: { ...prev.preferences, liveInShift: !prev.preferences?.liveInShift }}))}
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '16px' }}>Live-in Shift</div>
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Employee is available for live-in shifts with accommodation provided</div>
                                    </div>
                                </label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontWeight: '600' }}>Live-in Days Overview</div>
                                <div style={{ fontSize: '12px', color: '#166534', backgroundColor: '#dcfce7', padding: '4px 8px', borderRadius: '4px' }}>Next 30 Days</div>
                            </div>
                            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <FaCalendarDay color="#166534" size="24" />
                                <div>
                                    <div style={{ fontSize: '12px', color: '#14532d' }}>Live-in Days</div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#14532d' }}>0</div>
                                </div>
                            </div>
                            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>No live-in days scheduled for the next 30 days</p>
                        </>
                    )}

                    <div style={{ marginTop: '32px', textAlign: 'center' }}>
                        <button 
                            onClick={handleSaveAvailability} 
                            disabled={saving}
                            style={{ backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '8px', padding: '14px 32px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
                        >
                            {saving ? 'Saving...' : 'Save Availability'}
                        </button>
                    </div>
                </div>
            )}

            {/* Scheduled Shifts Content */}
            {activeTab === 'shifts' && (
                <div>
                    <WeekNavigator 
                        currentWeekStart={currentWeekStart}
                        onPrevWeek={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d); }}
                        onNextWeek={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d); }}
                        stats={currentWeekStats}
                    />

                    <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f3f4f6', padding: '4px', borderRadius: '8px', marginBottom: '24px' }}>
                        <button 
                            onClick={() => setShiftSubTab('currentWeek')}
                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', backgroundColor: shiftSubTab === 'currentWeek' ? '#0056b3' : 'transparent', color: shiftSubTab === 'currentWeek' ? 'white' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                        >
                            Current Week
                        </button>
                        <button 
                            onClick={() => setShiftSubTab('filteredView')}
                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', backgroundColor: shiftSubTab === 'filteredView' ? '#0056b3' : 'transparent', color: shiftSubTab === 'filteredView' ? 'white' : '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                        >
                            Filtered View
                        </button>
                    </div>
                    
                    {shiftSubTab === 'currentWeek' && (
                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '16px', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Week</div><div style={styles.miniStatVal}>{calculatedStats.weekPlanned.toFixed(1)}h</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Month</div><div style={styles.miniStatVal}>{calculatedStats.monthPlanned.toFixed(1)}h</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Quarter</div><div style={styles.miniStatVal}>{calculatedStats.quarterPlanned.toFixed(1)}h</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Year</div><div style={styles.miniStatVal}>{calculatedStats.yearPlanned.toFixed(1)}h</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Availability 30d</div><div style={styles.miniStatVal}>{calculatedStats.availability30d}</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Annual leave used</div><div style={styles.miniStatVal}>2.0d</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Leave left</div><div style={styles.miniStatVal}>{calculatedStats.annualLeaveLeft.toFixed(1)}d</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Sick days used</div><div style={styles.miniStatVal}>{calculatedStats.sickDaysUsed.toFixed(1)}d</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Cancelled shifts</div><div style={styles.miniStatVal}>{calculatedStats.cancelledShifts}</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Cancelled leave</div><div style={styles.miniStatVal}>{calculatedStats.cancelledLeave}</div></div>
                            <div style={styles.miniStatBox}><div style={styles.miniStatLabel}>Leave period</div><div style={styles.miniStatVal}>2026-04-01 &rarr; 2027-03-31</div></div>
                        </div>
                    )}

                    {displayShifts.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', color: '#6b7280' }}>
                            No shifts found for the selected view.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {displayShifts.map(shift => (
                                <div key={shift.id} style={{ 
                                    backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', 
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                            <span style={{ backgroundColor: '#e6f0fa', color: '#0056b3', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>Regular</span>
                                            <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#16a34a' }}></div>
                                                {shift.status === 'Scheduled' ? 'active' : shift.status}
                                            </span>
                                        </div>
                                        <div style={{ fontWeight: 'bold', color: '#111827', fontSize: '16px' }}>
                                            {shift.date ? shift.date.split('-').reverse().join('/') : ''}
                                        </div>
                                        <div style={{ display: 'flex', gap: '32px', marginTop: '16px' }}>
                                            <div>
                                                <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '2px' }}>Start</div>
                                                <div style={{ fontWeight: 'bold', color: '#374151', fontSize: '14px' }}>{shift.date ? shift.date.split('-').reverse().join('/') : ''}</div>
                                                <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: 'bold' }}>{shift.rawStartTime || shift.startTime}</div>
                                            </div>
                                            <div>
                                                <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '2px' }}>End</div>
                                                <div style={{ fontWeight: 'bold', color: '#374151', fontSize: '14px' }}>{shift.date ? shift.date.split('-').reverse().join('/') : ''}</div>
                                                <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: 'bold' }}>{shift.rawEndTime || shift.endTime}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ color: '#0056b3', fontSize: '18px', fontWeight: 'bold' }}>
                                            {(() => {
                                                if(!shift.startTime || !shift.endTime) return '0 hr';
                                                const [h1, m1] = shift.startTime.split(':').map(Number);
                                                const [h2, m2] = shift.endTime.split(':').map(Number);
                                                let diff = (h2 + m2/60) - (h1 + m1/60);
                                                if (diff < 0) diff += 24;
                                                return `${diff} hr`;
                                            })()}
                                        </div>
                                        <div style={{ color: '#9ca3af', fontSize: '12px' }}>Duration</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            {showQuickAdd && <QuickAddModal onClose={() => setShowQuickAdd(false)} onCreate={handleQuickAdd} />}
            {showCreateFromAvail && <CreateFromAvailabilityModal onClose={() => setShowCreateFromAvail(false)} />}
            {showBulkTimings && <BulkTimingsModal onClose={() => setShowBulkTimings(false)} onSave={handleBulkTimingsSave} />}
            
            {showCancellations && (
                <CancellationsModal 
                    onClose={() => setShowCancellations(false)} 
                    cancelledShifts={shifts.filter(s => s.status === 'Cancelled')}
                    cancelledLeaves={leaves.filter(l => l.status === 'Cancelled')}
                />
            )}

            {showSelectRange && (
                <SelectRangeModal 
                    initialStart={currentWeekStart}
                    initialEnd={currentWeekEnd}
                    onConfirm={(start, end) => {
                        setCurrentWeekStart(start);
                        setCurrentWeekEnd(end);
                        setShowSelectRange(false);
                    }}
                    onClose={() => setShowSelectRange(false)}
                />
            )}
        </div>
    );
}

const styles = {
    actionBtn: {
        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
        backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
        color: '#0056b3', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
        whiteSpace: 'nowrap'
    },
    mainTabBtn: {
        flex: 1, padding: '16px', border: 'none', borderRadius: '6px',
        fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
    },
    miniStatBox: {
        backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', minWidth: '110px', flexShrink: 0
    },
    miniStatLabel: {
        fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px'
    },
    miniStatVal: {
        fontSize: '14px', color: '#0056b3', fontWeight: 'bold'
    }
};
